import React from 'react';
import { ThemeBackgroundDecorations, ThemeDefinition } from './types';

const flatBackgroundDecorations: ThemeBackgroundDecorations = {
  elements: [],
};

export const flatTheme: ThemeDefinition = {
  id: 'flat',
  name: 'Bold Flat',
  description: 'Zero depth, bold colors, and geometric precision. No shadows, pure flat design with color as structure.',
  layoutMode: 'top-nav',
  headerPreferences: {
    showLogoDefault: true,
    showWordmarkDefault: true,
  },
  headerBehavior: {
    mobileHorizontalPadding: '4px',
    mobileInsetOffset: 'var(--shell-gap)',
  },
  sidebarBehavior: {
    compactHeightScrollFooter: false,
    zeroDesktopPanelPadding: false,
    navButtonGap: '2px',
    scrollerPaddingBottom: '0px',
    listPaddingBottom: '0px',
    itemPaddingBottom: '0px',
    hideStorageFooterOnMobile: false,
    mobileDrawerDocked: true,
    mobileDrawerBorderRadius: 'var(--radius-ui) var(--radius-ui) 0 0',
    mobileDrawerMarginTop: 'auto',
    mobileDrawerMarginBottom: '0px',
    mobileDrawerMaxHeight: '65vh',
    mobileDrawerWidth: '100%',
    mobileDrawerTop: 'auto',
    mobileDrawerLeft: '0',
    mobileDrawerRight: '0',
    mobileDrawerBottom: '0',
  },
  backgroundDecorations: flatBackgroundDecorations,
  layout: {
    desktop: {
      navPlacement: 'top',
      headerFrameMode: 'flush',
      mobileHeaderInset: false,
      showHeaderToggleOnMobile: false,
      showDesktopNavItems: true,
      showStorageHeaderWidget: true,
      headerVersionPlacement: 'desktop',
      headerTitleInset: '12px',
      headerToggleMode: 'menu',
      headerToggleWidth: '44px',
      headerToggleHeight: '44px',
      headerToggleRadius: 'var(--radius-ui)',
      headerToggleColor: '#111827',
      headerUpdateIndicatorMode: 'flat',
      headerBackground: 'var(--card, #fffdf5)',
      headerBorder: '2px solid var(--border)',
      headerBorderBottom: '2px solid var(--border)',
      headerPattern: 'none',
      headerBackdropFilter: 'none',
      headerBorderRadius: '0px',
      headerMenuRadius: '8px',
      headerMenuBorder: '2px solid var(--border)',
      headerMenuBackground: 'var(--card)',
      headerMenuShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      shellBackground: 'linear-gradient(180deg, var(--card) 0%, var(--background) 55%, var(--background) 100%)',
      mainPadding: '16px 16px 24px',
      mainMarginTop: '64px',
      contentPadding: '12px 16px',
      contentMaxWidth: '1400px',
      contentMarginInline: 'auto',
      contentFrameBackground: 'transparent',
      contentFrameBorder: 'none',
      contentFrameRadius: '0px',
      contentFrameShadow: 'none',
    },
    mobile: {
      navPlacement: 'top',
      headerFrameMode: 'flush',
      mobileHeaderInset: false,
      showHeaderToggleOnMobile: false,
      showDesktopNavItems: false,
      showStorageHeaderWidget: true,
      headerVersionPlacement: 'mobile',
      headerTitleInset: '0px',
      headerToggleMode: 'menu',
      headerToggleWidth: '44px',
      headerToggleHeight: '44px',
      headerToggleRadius: 'var(--radius-ui)',
      headerToggleColor: '#111827',
      headerUpdateIndicatorMode: 'flat',
      headerBackground: 'var(--card, #fffdf5)',
      headerBorder: '2px solid var(--border)',
      headerBorderBottom: '2px solid var(--border)',
      headerPattern: 'none',
      headerBackdropFilter: 'none',
      headerBorderRadius: '0px',
      headerMenuRadius: '8px',
      headerMenuBorder: '2px solid var(--border)',
      headerMenuBackground: 'var(--card)',
      headerMenuShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      shellBackground: 'linear-gradient(180deg, var(--card) 0%, var(--background) 55%, var(--background) 100%)',
      mainPadding: '8px 4px calc(20px + env(safe-area-inset-bottom))',
      mainMarginTop: '64px',
      contentPadding: '8px 4px',
      contentMaxWidth: '1400px',
      contentMarginInline: 'auto',
      contentFrameBackground: 'transparent',
      contentFrameBorder: 'none',
      contentFrameRadius: '0px',
      contentFrameShadow: 'none',
    },
  },
  preview: React.createElement('div', {
    key: 'preview-root',
    style: {
      padding: '20px',
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden',
      position: 'relative',
      border: '2px solid #E5E7EB',
      width: '100%',
      minHeight: 80,
    }
  }, [
    React.createElement('div', {
      key: 'b1',
      style: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: '#3B82F6',
        border: '2px solid #111827',
      }
    }),
    React.createElement('div', {
      key: 'b2',
      style: { width: 90, height: 16, borderRadius: '6px', backgroundColor: '#F3F4F6', border: '2px solid #111827' }
    })
  ]),
  tokens: {
    light: {
      'font-body': "'Archivo'",
      'font-display': "'Archivo'",
      background: '0 0% 100%', // Pure White
      foreground: '217 32% 10%', // Dark Gray 900 (#111827)
      card: '0 0% 100%',
      'card-foreground': '217 32% 10%',
      popover: '0 0% 100%',
      'popover-foreground': '217 32% 10%',
      primary: '217 92% 52%', // #3B82F6 - Bold Blue
      'primary-foreground': '210 40% 98%', // White
      secondary: '160 84% 39%', // #10B981 - Emerald
      'secondary-foreground': '0 0% 100%',
      muted: '210 40% 96.1%', // #F3F4F6 - Gray 100
      'muted-foreground': '215 13% 34%', // Gray 600
      accent: '38 92% 50%', // #F59E0B - Amber
      'accent-foreground': '0 0% 100%',
      destructive: '0 84% 60%', // Red
      'destructive-foreground': '0 0% 100%',
      'success': '142 71% 42%',
      'success-foreground': '0 0% 100%',
      'warning': '38 92% 50%',
      'warning-foreground': '0 0% 10%',
      'info': '199 89% 46%',
      'info-foreground': '0 0% 100%',
      border: '210 14% 90%', // #E5E7EB - Gray 200
      'border-strong': '217 32% 10%', // Dark Gray
      input: '210 40% 96%', // #F3F4F6 - Gray 100
      'input-border': '210 14% 90%', // Gray 200
      'input-border-hover': '217 92% 52%', // Primary Blue
      ring: '217 92% 52%', // Primary Blue
      radius: '0.25rem',
      'radius-ui': '8px', // 8px for consistency
      'radius-input': '8px',
      'radius-thumb': '8px',
      'border-weight': '2px',
      'nav-radius': '8px',
      'action-bar-gap': '6px',
      'action-bar-gap-compact': '6px',
      'action-bar-padding-y': '8px',
      'action-bar-padding-x': '0px',
      'chip-shadow': 'none',
      'chip-shadow-hover': 'none',
      'rating-chip-shadow': 'none',
      'rating-chip-shadow-hover': 'none',
      'status-chip-max-width': '140px',
      'rating-chip-max-width': '120px',
      'action-bar-foreground': '217 32% 10%',
      'action-bar-border': '210 14% 90%',
      'action-bar-hover-border': '217 92% 52%',
      'action-bar-disabled-foreground': '215 13% 34%',
      'snackbar-surface-background': 'rgba(255, 255, 255, 0.97)',
      'snackbar-surface-border': 'rgba(17, 24, 39, 0.16)',
      'snackbar-surface-foreground': '#111827',
      'overlay-backdrop-background': 'rgba(17, 24, 39, 0.42)',
      'overlay-backdrop-background-strong': 'rgba(17, 24, 39, 0.54)',
      'overlay-backdrop-filter': 'blur(4px)',
      'media-placeholder-background': '#111827',
      'media-placeholder-border': '1px solid rgba(107, 114, 128, 0.4)',
      'media-overlay-foreground': '#ffffff',
      'media-overlay-background': 'rgba(17, 24, 39, 0.66)',
      'media-overlay-background-strong': 'rgba(17, 24, 39, 0.82)',
      'media-overlay-danger-background': 'rgba(211, 47, 47, 0.95)',
      'media-overlay-selection-background': 'rgba(59, 130, 246, 0.24)',
      'media-overlay-delete-selection-background': 'rgba(220, 38, 38, 0.2)',
      'media-overlay-delete-indicator-background': 'rgba(220, 38, 38, 0.8)',
      'media-overlay-ignore-button-background': 'rgba(17, 24, 39, 0.7)',
      'media-overlay-text-shadow': 'drop-shadow(0px 2px 4px rgba(17, 24, 39, 0.5))',
      'video-modal-media-background': 'var(--card)',
      'video-modal-media-border': '2px solid var(--border-strong)',
      'video-modal-media-radius': 'var(--radius-ui)',
      'video-modal-media-max-height-mobile': '44vh',
      'video-modal-media-max-height-desktop': '58vh',
      'video-modal-media-overlay-gradient': 'linear-gradient(to bottom, rgba(17, 24, 39, 0.08) 0%, rgba(17, 24, 39, 0.44) 100%)',
      'video-modal-overlay-action-background': 'rgba(17, 24, 39, 0.76)',
      'video-modal-overlay-action-foreground': '#ffffff',
      'video-modal-overlay-action-size': '72px',
      'video-modal-overlay-action-icon-size': '48px',
      'video-modal-overlay-download-size': '90px',
      'video-modal-overlay-download-icon-size': '50px',
      'video-modal-overlay-corner-background': 'rgba(17, 24, 39, 0.62)',
      'video-modal-overlay-corner-foreground': '#ffffff',
      'video-modal-action-row-gap': '8px',
      'video-modal-action-row-padding-y': '2px',
      'video-modal-action-control-height': '28px',
      'video-modal-action-control-min-width': '28px',
      'video-modal-action-control-padding-x': '8px',
      'video-modal-content-gap': '10px',
      'video-modal-content-padding-mobile': '8px',
      'video-modal-content-padding-desktop': '12px',
      'video-modal-action-divider': 'var(--border-strong)',
      'video-modal-section-gap': '12px',
      'video-modal-delete-outline': 'var(--destructive)',
      'video-modal-delete-foreground': 'var(--destructive)',
      'video-modal-link-color': 'hsl(var(--primary))',
      'video-modal-info-bubble-background': 'var(--card)',
      'video-modal-info-bubble-foreground': 'var(--foreground)',
      'video-modal-info-bubble-border': '2px solid var(--border-strong)',
      'video-modal-info-bubble-shadow': 'none',
      'auth-splash-background': 'linear-gradient(135deg, rgba(59, 130, 246, 0.16) 0%, rgba(245, 158, 11, 0.18) 100%), #ffffff',
      'auth-surface-background': 'var(--card)',
      'auth-surface-border': '2px solid var(--border-strong)',
      'auth-surface-shadow': '0 10px 24px rgba(17, 24, 39, 0.08)',
      'auth-surface-backdrop-filter': 'none',
      'auth-surface-transform': 'none',
      'auth-surface-padding': '32px',
      'auth-title-font-weight': '700',
      'auth-title-font-size': '2.5rem',
      'auth-title-letter-spacing': 'normal',
      'auth-title-text-shadow': 'none',
      'auth-subtitle-font-size': '1rem',
      'auth-button-text-transform': 'none',
      'auth-button-letter-spacing': 'normal',
      'linear-decor-blob-primary': 'radial-gradient(circle, rgba(59, 130, 246, 0.28) 0%, transparent 70%)',
      'linear-decor-blob-secondary': 'radial-gradient(circle, rgba(245, 158, 11, 0.22) 0%, transparent 70%)',
      'linear-decor-top-rail': 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.38), transparent)',
      'audio-chip-radius': 'var(--radius-ui)',
      'audio-chip-bg': 'transparent',
      'audio-chip-foreground': 'var(--foreground)',
      'audio-chip-icon': 'var(--foreground)',
      'video-chip-radius': 'var(--radius-ui)',
      'video-chip-bg': 'transparent',
      'video-chip-foreground': 'var(--foreground)',
      'video-chip-icon': 'var(--foreground)',
      'fab-primary-bg': 'var(--primary)',
      'fab-primary-fg': 'var(--primary-foreground)',
      'fab-primary-hover-bg': 'var(--primary)',
      'fab-primary-hover-fg': 'var(--primary-foreground)',
      'fab-secondary-bg': 'var(--secondary)',
      'fab-secondary-fg': 'var(--secondary-foreground)',
      'fab-secondary-hover-bg': 'var(--secondary)',
      'fab-secondary-hover-fg': 'var(--secondary-foreground)',
      'fab-error-bg': 'var(--destructive)',
      'fab-error-fg': 'var(--destructive-foreground)',
      'fab-error-hover-bg': 'var(--destructive)',
      'fab-error-hover-fg': 'var(--destructive-foreground)',
      'selection-fab-size': '56px',
      'selection-fab-radius': '12px',
      'selection-fab-border': '2px solid var(--foreground)',
      'selection-fab-shadow': 'none',
      'selection-fab-shadow-hover': 'none',
      'selection-fab-shadow-active': 'none',
      'selection-fab-icon-size': '22px',
      'selection-fab-badge-background': 'var(--card)',
      'selection-fab-badge-foreground': 'var(--foreground)',
      'selection-fab-badge-border': '2px solid var(--foreground)',
      'selection-fab-badge-shadow': 'none',
      'selection-fab-badge-radius': '999px',
      'nav-hover-style': 'flat-solid',
      'shadow-soft': 'none',
      'shadow-hard': 'none',
      'shadow-hard-hover': 'none',
      'transition-smooth': 'all 0.2s ease',
      'nav-border': '2px solid #111827',
      'nav-shadow': 'none',
      'nav-item-border': '2px solid #111827',
      'nav-item-border-selected': '2px solid #111827',
      'nav-item-border-hover': '2px solid #111827',
      'nav-item-bg': '#FFFFFF',
      'nav-item-bg-selected': '#3B82F6',
      'nav-item-bg-hover': 'rgba(59, 130, 246, 0.08)',
      'nav-item-shadow': 'none',
      'nav-item-shadow-selected': 'none',
      'nav-item-shadow-hover': 'none',
      'header-nav-active-color': 'hsl(var(--primary))',
      'header-nav-default-color': 'var(--muted-foreground)',
      'header-subnav-active-color': 'hsl(var(--primary))',
      'header-update-indicator-width': '34px',
      'header-update-indicator-height': '34px',
      'header-update-indicator-radius': 'var(--radius-ui)',
      'header-update-indicator-foreground': 'var(--warning-foreground)',
      'header-update-indicator-background': 'var(--warning)',
      'header-update-indicator-border': '2px solid var(--foreground)',
      'header-update-indicator-shadow': 'none',
      'mobile-subnav-surface-background': 'var(--card)',
      'mobile-subnav-surface-border-top': '2px solid var(--border)',
      'mobile-subnav-surface-radius': '0',
      'mobile-subnav-surface-margin-bottom': '0',
      'mobile-subnav-surface-padding-bottom': '8px',
      'mobile-subnav-item-border': 'var(--nav-item-border)',
      'mobile-subnav-item-border-selected': 'var(--nav-item-border-selected)',
      'mobile-subnav-item-radius': 'var(--radius-ui)',
      'mobile-subnav-item-text-transform': 'none',
      'mobile-subnav-item-letter-spacing': 'normal',
      'mobile-primary-nav-surface-background': 'var(--card)',
      'mobile-primary-nav-surface-border-top': '2px solid var(--border)',
      'mobile-primary-nav-surface-radius': '0',
      'mobile-primary-nav-surface-shadow': 'var(--nav-shadow)',
      'mobile-primary-nav-active-color': 'var(--primary)',
      'mobile-primary-nav-active-background': 'var(--muted)',
      'mobile-primary-nav-label-font-size': '0.65rem',
      'mobile-primary-nav-label-text-transform': 'none',
      'mobile-primary-nav-label-letter-spacing': 'normal',
      'channel-meta-chip-background': 'var(--card)',
      'channel-meta-chip-foreground': '#111827',
      'channel-meta-chip-border': '2px solid var(--border-strong)',
      'channel-meta-chip-shadow': 'none',
      'channel-meta-chip-icon': 'var(--foreground)',
      'rating-chip-border': '2px solid var(--foreground)',
      'nav-item-transform': 'translate(0, 0)',
      'nav-item-transform-hover': 'translate(0, 0)',
      'transition-bouncy': 'ease',
      'card-hover-transform': 'none',
      'nav-item-text-selected': '#FFFFFF',
    },
    dark: {
      'font-body': "'Archivo'",
      'font-display': "'Archivo'",
      background: '222 47% 11%',
      foreground: '0 0% 100%',
      card: '222 47% 12%',
      'card-foreground': '0 0% 100%',
      popover: '222 47% 11%',
      'popover-foreground': '0 0% 100%',
      primary: '217 92% 60%',
      'primary-foreground': '0 0% 100%',
      secondary: '160 84% 45%',
      'secondary-foreground': '0 0% 100%',
      muted: '222 47% 18%',
      'muted-foreground': '215 20% 65%',
      accent: '38 92% 55%',
      'accent-foreground': '0 0% 0%',
      destructive: '0 84% 65%',
      'destructive-foreground': '0 0% 100%',
      'success': '142 62% 45%',
      'success-foreground': '0 0% 10%',
      'warning': '38 85% 55%',
      'warning-foreground': '0 0% 10%',
      'info': '199 80% 55%',
      'info-foreground': '0 0% 10%',
      border: '222 47% 20%',
      'border-strong': '0 0% 100%',
      input: '222 47% 15%',
      'input-border': '222 47% 20%',
      'input-border-hover': '217 92% 60%',
      ring: '217 92% 60%',
      radius: '0.25rem',
      'radius-ui': '2px',
      'radius-input': '4px',
      'radius-thumb': '4px',
      'border-weight': '1px',
      'nav-radius': '2px',
      'action-bar-gap': '6px',
      'action-bar-gap-compact': '6px',
      'action-bar-padding-y': '8px',
      'action-bar-padding-x': '0px',
      'chip-shadow': 'none',
      'chip-shadow-hover': 'none',
      'rating-chip-shadow': 'none',
      'rating-chip-shadow-hover': 'none',
      'status-chip-max-width': '140px',
      'rating-chip-max-width': '120px',
      'action-bar-foreground': '0 0% 100%',
      'action-bar-border': '222 47% 20',
      'action-bar-hover-border': '0 0% 100%',
      'action-bar-disabled-foreground': '215 20% 65%',
      'snackbar-surface-background': 'rgba(17, 24, 39, 0.97)',
      'snackbar-surface-border': 'rgba(255, 255, 255, 0.14)',
      'snackbar-surface-foreground': '#ffffff',
      'overlay-backdrop-background': 'rgba(0, 0, 0, 0.5)',
      'overlay-backdrop-background-strong': 'rgba(0, 0, 0, 0.6)',
      'overlay-backdrop-filter': 'blur(4px)',
      'media-placeholder-background': '#111827',
      'media-placeholder-border': '1px solid rgba(107, 114, 128, 0.4)',
      'media-overlay-foreground': '#ffffff',
      'media-overlay-background': 'rgba(17, 24, 39, 0.72)',
      'media-overlay-background-strong': 'rgba(17, 24, 39, 0.86)',
      'media-overlay-danger-background': 'rgba(211, 47, 47, 0.95)',
      'media-overlay-selection-background': 'rgba(59, 130, 246, 0.28)',
      'media-overlay-delete-selection-background': 'rgba(220, 38, 38, 0.22)',
      'media-overlay-delete-indicator-background': 'rgba(220, 38, 38, 0.82)',
      'media-overlay-ignore-button-background': 'rgba(17, 24, 39, 0.74)',
      'media-overlay-text-shadow': 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.55))',
      'video-modal-media-background': 'var(--card)',
      'video-modal-media-border': '2px solid var(--border-strong)',
      'video-modal-media-radius': 'var(--radius-ui)',
      'video-modal-media-max-height-mobile': '44vh',
      'video-modal-media-max-height-desktop': '58vh',
      'video-modal-media-overlay-gradient': 'linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.5) 100%)',
      'video-modal-overlay-action-background': 'rgba(0, 0, 0, 0.76)',
      'video-modal-overlay-action-foreground': '#ffffff',
      'video-modal-overlay-action-size': '72px',
      'video-modal-overlay-action-icon-size': '48px',
      'video-modal-overlay-download-size': '90px',
      'video-modal-overlay-download-icon-size': '50px',
      'video-modal-overlay-corner-background': 'rgba(0, 0, 0, 0.62)',
      'video-modal-overlay-corner-foreground': '#ffffff',
      'video-modal-action-row-gap': '8px',
      'video-modal-action-row-padding-y': '2px',
      'video-modal-action-control-height': '28px',
      'video-modal-action-control-min-width': '28px',
      'video-modal-action-control-padding-x': '8px',
      'video-modal-content-gap': '10px',
      'video-modal-content-padding-mobile': '8px',
      'video-modal-content-padding-desktop': '12px',
      'video-modal-action-divider': 'var(--border-strong)',
      'video-modal-section-gap': '12px',
      'video-modal-delete-outline': 'var(--destructive)',
      'video-modal-delete-foreground': 'var(--destructive)',
      'video-modal-link-color': 'hsl(var(--primary))',
      'video-modal-info-bubble-background': 'var(--card)',
      'video-modal-info-bubble-foreground': 'var(--foreground)',
      'video-modal-info-bubble-border': '2px solid var(--border-strong)',
      'video-modal-info-bubble-shadow': 'none',
      'auth-splash-background': 'linear-gradient(135deg, rgba(59, 130, 246, 0.24) 0%, rgba(16, 185, 129, 0.18) 100%), #111827',
      'auth-surface-background': 'rgba(17, 24, 39, 0.94)',
      'auth-surface-border': '2px solid var(--border-strong)',
      'auth-surface-shadow': '0 16px 40px rgba(0, 0, 0, 0.35)',
      'auth-surface-backdrop-filter': 'none',
      'auth-surface-transform': 'none',
      'auth-surface-padding': '32px',
      'auth-title-font-weight': '700',
      'auth-title-font-size': '2.5rem',
      'auth-title-letter-spacing': 'normal',
      'auth-title-text-shadow': 'none',
      'auth-subtitle-font-size': '1rem',
      'auth-button-text-transform': 'none',
      'auth-button-letter-spacing': 'normal',
      'linear-decor-blob-primary': 'radial-gradient(circle, rgba(59, 130, 246, 0.32) 0%, transparent 70%)',
      'linear-decor-blob-secondary': 'radial-gradient(circle, rgba(16, 185, 129, 0.24) 0%, transparent 70%)',
      'linear-decor-top-rail': 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.42), transparent)',
      'audio-chip-radius': 'var(--radius-ui)',
      'audio-chip-bg': 'transparent',
      'audio-chip-foreground': 'var(--foreground)',
      'audio-chip-icon': 'var(--foreground)',
      'video-chip-radius': 'var(--radius-ui)',
      'video-chip-bg': 'transparent',
      'video-chip-foreground': 'var(--foreground)',
      'video-chip-icon': 'var(--foreground)',
      'fab-primary-bg': 'var(--primary)',
      'fab-primary-fg': 'var(--primary-foreground)',
      'fab-primary-hover-bg': 'var(--primary)',
      'fab-primary-hover-fg': 'var(--primary-foreground)',
      'fab-secondary-bg': 'var(--secondary)',
      'fab-secondary-fg': 'var(--secondary-foreground)',
      'fab-secondary-hover-bg': 'var(--secondary)',
      'fab-secondary-hover-fg': 'var(--secondary-foreground)',
      'fab-error-bg': 'var(--destructive)',
      'fab-error-fg': 'var(--destructive-foreground)',
      'fab-error-hover-bg': 'var(--destructive)',
      'fab-error-hover-fg': 'var(--destructive-foreground)',
      'selection-fab-size': '56px',
      'selection-fab-radius': '8px',
      'selection-fab-border': '2px solid #FFFFFF',
      'selection-fab-shadow': 'none',
      'selection-fab-shadow-hover': 'none',
      'selection-fab-shadow-active': 'none',
      'selection-fab-icon-size': '22px',
      'selection-fab-badge-background': 'var(--card)',
      'selection-fab-badge-foreground': '#ffffff',
      'selection-fab-badge-border': '2px solid #FFFFFF',
      'selection-fab-badge-shadow': 'none',
      'selection-fab-badge-radius': '999px',
      'nav-hover-style': 'flat-solid',
      'shadow-soft': 'none',
      'shadow-hard': 'none',
      'shadow-hard-hover': 'none',
      'transition-smooth': 'all 0.2s ease',
      'nav-border': '2px solid #FFFFFF',
      'nav-shadow': 'none',
      'nav-item-border': '2px solid #FFFFFF',
      'nav-item-border-selected': '2px solid #FFFFFF',
      'nav-item-border-hover': '2px solid #FFFFFF',
      'nav-item-bg': '#111827',
      'nav-item-bg-selected': '#3B82F6',
      'nav-item-bg-hover': 'rgba(59, 130, 246, 0.15)',
      'nav-item-shadow': 'none',
      'nav-item-shadow-selected': 'none',
      'nav-item-shadow-hover': 'none',
      'header-nav-active-color': 'hsl(var(--primary))',
      'header-nav-default-color': 'var(--muted-foreground)',
      'header-subnav-active-color': 'hsl(var(--primary))',
      'header-update-indicator-width': '34px',
      'header-update-indicator-height': '34px',
      'header-update-indicator-radius': 'var(--radius-ui)',
      'header-update-indicator-foreground': 'var(--warning-foreground)',
      'header-update-indicator-background': 'var(--warning)',
      'header-update-indicator-border': '2px solid var(--foreground)',
      'header-update-indicator-shadow': 'none',
      'mobile-subnav-surface-background': 'var(--card)',
      'mobile-subnav-surface-border-top': '2px solid var(--border)',
      'mobile-subnav-surface-radius': '0',
      'mobile-subnav-surface-margin-bottom': '0',
      'mobile-subnav-surface-padding-bottom': '8px',
      'mobile-subnav-item-border': 'var(--nav-item-border)',
      'mobile-subnav-item-border-selected': 'var(--nav-item-border-selected)',
      'mobile-subnav-item-radius': 'var(--radius-ui)',
      'mobile-subnav-item-text-transform': 'none',
      'mobile-subnav-item-letter-spacing': 'normal',
      'mobile-primary-nav-surface-background': 'var(--card)',
      'mobile-primary-nav-surface-border-top': '2px solid var(--border)',
      'mobile-primary-nav-surface-radius': '0',
      'mobile-primary-nav-surface-shadow': 'var(--nav-shadow)',
      'mobile-primary-nav-active-color': 'var(--primary)',
      'mobile-primary-nav-active-background': 'var(--muted)',
      'mobile-primary-nav-label-font-size': '0.65rem',
      'mobile-primary-nav-label-text-transform': 'none',
      'mobile-primary-nav-label-letter-spacing': 'normal',
      'channel-meta-chip-background': 'var(--card)',
      'channel-meta-chip-foreground': '#ffffff',
      'channel-meta-chip-border': '1px solid rgba(255, 255, 255, 0.24)',
      'channel-meta-chip-shadow': 'none',
      'channel-meta-chip-icon': 'var(--foreground)',
      'rating-chip-border': '1px solid rgba(255, 255, 255, 0.24)',
      'nav-item-transform': 'translate(0, 0)',
      'nav-item-transform-hover': 'translate(0, 0)',
      'transition-bouncy': 'ease',
      'card-hover-transform': 'none',
      'nav-item-text-selected': '#FFFFFF',
    },
  },
};

